import { FC, useMemo, useState } from "react";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Paper,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import SendIcon from "@mui/icons-material/Send";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { useQuery } from "@tanstack/react-query";
import { useUserStore } from "../../../../../stores/user.store";
import { useAppTheme } from "../../../../../utils/theme";
import { Program } from "../../../../../domain/program.interface";
import {
	AnalyticsGeneratedReport,
	AnalyticsReportPreview,
	analyticsService,
} from "../../../../../services/analytics.service";

interface AnalyticsAssistantTabProps {
	programs: Program[];
}

interface Bubble {
	role: "USER" | "ASSISTANT";
	content: string;
	report?: AnalyticsGeneratedReport;
	reportId?: string;
}

const PREDEFINED: Array<{ label: string; prompt: string; hint: string }> = [
	{
		label: "Enrollment Summary",
		prompt: "Generate an Enrollment Summary report.",
		hint: "One row per enrollment · lifetime totals",
	},
	{
		label: "Program Activity Summary",
		prompt: "Generate a Program Activity Summary report.",
		hint: "Enrollment + incentive + visit KPIs",
	},
	{
		label: "Visit Detail",
		prompt: "Generate a Visit Detail report.",
		hint: "One row per visit + analytes",
	},
];

// Security: report download URLs come from the analytics service (S3 presigned
// URLs). Before handing one to a navigation/embed sink (window.open / iframe
// src), allowlist the scheme so a "javascript:" or "data:" URL can never reach
// the sink and execute script (XSS). Only http(s) downloads are ever expected.
const isSafeDownloadUrl = (raw?: string): boolean => {
	if (!raw) return false;
	try {
		const u = new URL(raw, window.location.origin);
		return u.protocol === "https:" || u.protocol === "http:";
	} catch {
		return false;
	}
};

const AnalyticsAssistantTab: FC<AnalyticsAssistantTabProps> = ({
	programs,
}) => {
	const theme = useAppTheme();
	const { userInfo } = useUserStore();
	const userId = userInfo?.id ?? "";

	const c = {
		band: theme.palette.surface.main,
		border: theme.palette.outline.light,
		muted: theme.palette.textSecondary.main,
		ink: theme.palette.textPrimary.main,
		accent: theme.palette.primary.main,
		accentBand: theme.palette.surface.dark,
	};

	// Categorical palette for cards/charts, derived from tenant theme tokens so
	// it follows multi-tenant branding (no hardcoded literals).
	const chartPalette = [
		theme.palette.primary.main,
		theme.palette.secondary.main,
		theme.palette.accent.main,
		theme.palette.warning.main,
		theme.palette.error.main,
		theme.palette.primary.dark,
		theme.palette.secondary.dark,
		theme.palette.accent.dark,
	];
	const colorAt = (i: number): string => chartPalette[i % chartPalette.length];

	const programRefs = useMemo(
		() => programs.map((p) => ({ id: p._id, name: p.name })),
		[programs],
	);

	const [messages, setMessages] = useState<Bubble[]>([]);
	const [chatSessionId, setChatSessionId] = useState<string>("");
	const [input, setInput] = useState<string>("");
	const [sending, setSending] = useState<boolean>(false);
	const [emailTarget, setEmailTarget] = useState<string | null>(null);
	const [emailRecipients, setEmailRecipients] = useState<string>("");
	const [emailBusy, setEmailBusy] = useState<boolean>(false);
	const [toast, setToast] = useState<string>("");
	const [pdfPreviewId, setPdfPreviewId] = useState<string | null>(null);

	const chatsQuery = useQuery({
		queryKey: ["analytics-chats", userId],
		queryFn: () => analyticsService.listChats(userId),
		enabled: Boolean(userId),
		staleTime: 1000 * 30,
	});

	const send = async (text: string): Promise<void> => {
		const message = text.trim();
		if (!message || sending) return;
		setInput("");
		setMessages((prev) => [...prev, { role: "USER", content: message }]);
		setSending(true);
		try {
			const res = await analyticsService.sendMessage({
				message,
				chatSessionId: chatSessionId || undefined,
				userId,
				programs: programRefs,
			});
			setChatSessionId(res.chatSessionId);
			setMessages((prev) => [
				...prev,
				{
					role: "ASSISTANT",
					content: res.message,
					report: res.report,
				},
			]);
			chatsQuery.refetch();
		} catch (err) {
			const msg =
				(err as { message?: string })?.message ??
				"Something went wrong contacting the assistant.";
			setMessages((prev) => [
				...prev,
				{ role: "ASSISTANT", content: `⚠️ ${msg}` },
			]);
		} finally {
			setSending(false);
		}
	};

	const newChat = (): void => {
		setMessages([]);
		setChatSessionId("");
		setInput("");
	};

	const loadChat = async (id: string): Promise<void> => {
		try {
			const session = await analyticsService.getChat(id);
			setChatSessionId(session._id);
			setMessages(
				(session.messages ?? []).map((m) => ({
					role: m.role,
					content: m.content,
					reportId: m.reportId,
				})),
			);
		} catch {
			setToast("Could not load that conversation.");
		}
	};

	const deleteChat = async (id: string): Promise<void> => {
		try {
			await analyticsService.deleteChat(id);
			if (id === chatSessionId) newChat();
			chatsQuery.refetch();
			setToast("Conversation deleted.");
		} catch {
			setToast("Could not delete that conversation.");
		}
	};

	const openReport = async (
		reportId?: string,
		knownUrl?: string,
	): Promise<void> => {
		try {
			if (knownUrl) {
				if (isSafeDownloadUrl(knownUrl)) {
					window.open(knownUrl, "_blank", "noopener");
				} else {
					setToast("Could not get the download link.");
				}
				return;
			}
			if (!reportId) return;
			const { downloadUrl } =
				await analyticsService.getReportDownload(reportId);
			if (isSafeDownloadUrl(downloadUrl)) {
				window.open(downloadUrl, "_blank", "noopener");
			} else {
				setToast("Could not get the download link.");
			}
		} catch {
			setToast("Could not get the download link.");
		}
	};

	const handleSendEmail = async (): Promise<void> => {
		if (!emailTarget) return;
		const recipients = emailRecipients
			.split(",")
			.map((s) => s.trim())
			.filter(Boolean);
		if (recipients.length === 0) return;
		setEmailBusy(true);
		try {
			const res = await analyticsService.emailReport(
				emailTarget,
				recipients,
				userId,
			);
			setToast(`Emailed to ${res.sent} recipient(s).`);
			setEmailTarget(null);
			setEmailRecipients("");
		} catch {
			setToast("Email failed to send.");
		} finally {
			setEmailBusy(false);
		}
	};

	// The agent chooses how the result is shown inline: a single stat card, a
	// grid of KPI cards, a pie/bar chart, or a compact table. The cards/charts
	// use a tinted, colored surface so they stand out from the chat. Full data
	// is always in the download.
	const ResultPreview: FC<{ preview?: AnalyticsReportPreview }> = ({
		preview,
	}) => {
		if (!preview) return null;

		if (preview.kind === "SCALAR") {
			const accent = colorAt(0);
			return (
				<Box
					sx={{
						mt: 1.25,
						p: 2,
						borderRadius: 2,
						borderLeft: `4px solid ${accent}`,
						border: `1px solid ${alpha(accent, 0.35)}`,
						bgcolor: alpha(accent, 0.1),
						display: "inline-flex",
						flexDirection: "column",
						minWidth: 180,
					}}
				>
					<Typography
						sx={{
							fontSize: 12,
							color: c.muted,
							textTransform: "uppercase",
							letterSpacing: 0.4,
						}}
					>
						{preview.valueLabel}
					</Typography>
					<Typography
						sx={{
							fontSize: 38,
							fontWeight: 800,
							color: accent,
							lineHeight: 1.1,
						}}
					>
						{String(preview.value)}
					</Typography>
				</Box>
			);
		}

		if (preview.kind === "KPI_GRID") {
			return (
				<Box
					sx={{
						mt: 1.25,
						display: "grid",
						gridTemplateColumns: {
							xs: "repeat(2, 1fr)",
							sm: "repeat(auto-fill, minmax(140px, 1fr))",
						},
						gap: 1,
					}}
				>
					{(preview.metrics ?? []).map((m, i) => {
						const accent = colorAt(i);
						return (
							<Box
								key={m.label}
								sx={{
									p: 1.25,
									borderRadius: 2,
									borderLeft: `4px solid ${accent}`,
									border: `1px solid ${alpha(accent, 0.35)}`,
									bgcolor: alpha(accent, 0.1),
									minWidth: 0,
								}}
							>
								<Typography
									sx={{ fontSize: 24, fontWeight: 800, color: accent }}
								>
									{String(m.value)}
								</Typography>
								<Typography
									sx={{
										fontSize: 12,
										color: c.muted,
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
									}}
								>
									{m.label}
								</Typography>
							</Box>
						);
					})}
				</Box>
			);
		}

		if (preview.kind === "PIE" || preview.kind === "BAR") {
			const data = (preview.metrics ?? []).map((m) => ({
				name: m.label,
				value: typeof m.value === "number" ? m.value : Number(m.value) || 0,
			}));
			return (
				<Box
					sx={{
						mt: 1.25,
						p: 1.5,
						borderRadius: 2,
						border: `1px solid ${c.border}`,
						bgcolor: alpha(c.accent, 0.06),
					}}
				>
					<Box sx={{ width: "100%", height: 240 }}>
						<ResponsiveContainer width="100%" height="100%">
							{preview.kind === "PIE" ? (
								<PieChart>
									<Tooltip />
									<Pie
										data={data}
										dataKey="value"
										nameKey="name"
										innerRadius={50}
										outerRadius={90}
										paddingAngle={1}
									>
										{data.map((entry, i) => (
											<Cell key={entry.name} fill={colorAt(i)} />
										))}
									</Pie>
								</PieChart>
							) : (
								<BarChart
									data={data}
									margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
								>
									<CartesianGrid strokeDasharray="3 3" vertical={false} />
									<XAxis dataKey="name" />
									<YAxis allowDecimals={false} />
									<Tooltip />
									<Bar dataKey="value">
										{data.map((entry, i) => (
											<Cell key={entry.name} fill={colorAt(i)} />
										))}
									</Bar>
								</BarChart>
							)}
						</ResponsiveContainer>
					</Box>
					{/* Accessible / always-rendered legend (theme-colored swatches). */}
					<Stack
						direction="row"
						flexWrap="wrap"
						sx={{ gap: 1, mt: 1 }}
						aria-label="chart legend"
					>
						{data.map((entry, i) => (
							<Stack
								key={entry.name}
								direction="row"
								alignItems="center"
								spacing={0.75}
							>
								<Box
									sx={{
										width: 12,
										height: 12,
										borderRadius: 0.5,
										bgcolor: colorAt(i),
									}}
								/>
								<Typography sx={{ fontSize: 12, color: c.ink }}>
									{entry.name}: <b>{entry.value}</b>
								</Typography>
							</Stack>
						))}
					</Stack>
				</Box>
			);
		}

		const columns = preview.columns ?? [];
		const rows = preview.rows ?? [];
		return (
			<Box
				sx={{
					mt: 1.25,
					border: `1px solid ${c.border}`,
					borderRadius: 2,
					overflowX: "auto",
				}}
			>
				<Box
					component="table"
					sx={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}
				>
					<Box component="thead">
						<Box component="tr">
							{columns.map((col) => (
								<Box
									component="th"
									key={col}
									sx={{
										textAlign: "left",
										p: 1,
										bgcolor: c.band,
										color: c.ink,
										fontWeight: 700,
										whiteSpace: "nowrap",
										borderBottom: `1px solid ${c.border}`,
									}}
								>
									{col}
								</Box>
							))}
						</Box>
					</Box>
					<Box component="tbody">
						{rows.map((r, i) => (
							<Box component="tr" key={i}>
								{columns.map((col) => (
									<Box
										component="td"
										key={col}
										sx={{
											p: 1,
											color: c.ink,
											whiteSpace: "nowrap",
											borderBottom: `1px solid ${c.border}`,
										}}
									>
										{r[col]}
									</Box>
								))}
							</Box>
						))}
					</Box>
				</Box>
				{preview.totalRows && preview.totalRows > rows.length ? (
					<Typography sx={{ fontSize: 12, color: c.muted, p: 1 }}>
						Showing {rows.length} of {preview.totalRows} rows — full data in the
						download.
					</Typography>
				) : null}
			</Box>
		);
	};

	const ReportActions: FC<{ bubble: Bubble }> = ({ bubble }) => {
		const report = bubble.report;
		const reportId = report?.id ?? bubble.reportId;
		if (!reportId) return null;
		// isSafeDownloadUrl also gates the <iframe src> below: an unsafe scheme
		// (javascript:/data:) never renders a preview.
		const isPdf =
			report?.format === "PDF" && isSafeDownloadUrl(report?.downloadUrl);
		const previewOpen = pdfPreviewId === reportId;
		return (
			<>
				<Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
					<Button
						size="small"
						variant="contained"
						startIcon={<FileDownloadOutlinedIcon />}
						onClick={() => openReport(reportId, report?.downloadUrl)}
					>
						Download
					</Button>
					<Button
						size="small"
						variant="outlined"
						startIcon={<MailOutlineIcon />}
						onClick={() => setEmailTarget(reportId)}
					>
						Email
					</Button>
					{isPdf ? (
						<Button
							size="small"
							variant="text"
							startIcon={<PictureAsPdfOutlinedIcon />}
							onClick={() => setPdfPreviewId(previewOpen ? null : reportId)}
						>
							{previewOpen ? "Hide document" : "Preview document"}
						</Button>
					) : null}
				</Stack>
				{isPdf && previewOpen ? (
					<Box
						sx={{
							mt: 1.25,
							border: `1px solid ${c.border}`,
							borderRadius: 2,
							overflow: "hidden",
							bgcolor: c.band,
						}}
					>
						<iframe
							title={report?.fileName}
							src={report?.downloadUrl}
							style={{ width: "100%", height: 420, border: "none" }}
						/>
					</Box>
				) : null}
			</>
		);
	};

	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: { xs: "1fr", md: "minmax(0,1fr) 300px" },
				gap: 3,
				minWidth: 0,
			}}
		>
			{/* Main: predefined prompts + chat */}
			<Stack spacing={2} sx={{ minWidth: 0 }}>
				<Paper
					variant="outlined"
					sx={{ borderColor: c.border, borderRadius: 3, p: 2.5 }}
				>
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="center"
						sx={{ mb: 1.5 }}
					>
						<Typography sx={{ fontSize: 13, fontWeight: 700, color: c.muted }}>
							PREDEFINED REPORTS
						</Typography>
						<Button
							size="small"
							startIcon={<AddCommentOutlinedIcon />}
							onClick={newChat}
						>
							New chat
						</Button>
					</Stack>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: {
								xs: "1fr",
								sm: "repeat(3, minmax(0,1fr))",
							},
							gap: 1.5,
						}}
					>
						{PREDEFINED.map((p) => (
							<Paper
								key={p.label}
								variant="outlined"
								onClick={() => send(p.prompt)}
								sx={{
									borderColor: c.border,
									borderRadius: 2,
									p: 1.5,
									cursor: sending ? "default" : "pointer",
									opacity: sending ? 0.6 : 1,
									"&:hover": { bgcolor: c.band },
								}}
							>
								<Typography
									sx={{ fontSize: 15, fontWeight: 600, color: c.ink }}
								>
									{p.label}
								</Typography>
								<Typography sx={{ fontSize: 12, color: c.muted, mt: 0.25 }}>
									{p.hint}
								</Typography>
							</Paper>
						))}
					</Box>
				</Paper>

				<Paper
					variant="outlined"
					sx={{
						borderColor: c.border,
						borderRadius: 3,
						display: "flex",
						flexDirection: "column",
						minHeight: 420,
					}}
				>
					<Box sx={{ flex: 1, overflowY: "auto", p: 2.5 }}>
						{messages.length === 0 ? (
							<Stack
								alignItems="center"
								justifyContent="center"
								spacing={1}
								sx={{
									height: "100%",
									color: c.muted,
									textAlign: "center",
									py: 6,
								}}
							>
								<AutoAwesomeIcon sx={{ fontSize: 40, color: c.accent }} />
								<Typography sx={{ fontSize: 16 }}>
									Ask for a report, or pick a predefined one above.
								</Typography>
								<Typography sx={{ fontSize: 13 }}>
									e.g. “Enrollment summary for the Maine CM program, active
									members”.
								</Typography>
							</Stack>
						) : (
							<Stack spacing={1.5}>
								{messages.map((m, i) => (
									<Box
										key={i}
										sx={{
											alignSelf: m.role === "USER" ? "flex-end" : "flex-start",
											maxWidth: "85%",
											bgcolor: m.role === "USER" ? c.accentBand : c.band,
											border: `1px solid ${c.border}`,
											borderRadius: 2,
											px: 1.75,
											py: 1.25,
										}}
									>
										<Typography
											sx={{
												fontSize: 15,
												color: c.ink,
												whiteSpace: "pre-wrap",
											}}
										>
											{m.content}
										</Typography>
										<ResultPreview preview={m.report?.preview} />
										<ReportActions bubble={m} />
									</Box>
								))}
								{sending && (
									<Stack
										direction="row"
										spacing={1}
										alignItems="center"
										sx={{ color: c.muted }}
									>
										<CircularProgress size={14} />
										<Typography sx={{ fontSize: 14 }}>Thinking…</Typography>
									</Stack>
								)}
							</Stack>
						)}
					</Box>
					<Divider />
					<Stack direction="row" spacing={1} sx={{ p: 1.5 }}>
						<TextField
							fullWidth
							size="small"
							placeholder="Describe the report you need…"
							value={input}
							onChange={(e) => setInput(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter" && !e.shiftKey) {
									e.preventDefault();
									send(input);
								}
							}}
							disabled={sending}
						/>
						<IconButton
							color="primary"
							onClick={() => send(input)}
							disabled={sending || !input.trim()}
							aria-label="Send"
						>
							<SendIcon />
						</IconButton>
					</Stack>
				</Paper>
			</Stack>

			{/* History */}
			<Paper
				variant="outlined"
				sx={{ borderColor: c.border, borderRadius: 3, p: 2, minWidth: 0 }}
			>
				<Typography
					sx={{ fontSize: 13, fontWeight: 700, color: c.muted, mb: 1 }}
				>
					CHAT HISTORY
				</Typography>
				{chatsQuery.isLoading ? (
					<CircularProgress size={18} />
				) : (chatsQuery.data ?? []).length === 0 ? (
					<Typography sx={{ fontSize: 13, color: c.muted }}>
						No conversations yet.
					</Typography>
				) : (
					<Stack spacing={1}>
						{(chatsQuery.data ?? []).map((s) => (
							<Box
								key={s._id}
								sx={{
									display: "flex",
									alignItems: "flex-start",
									gap: 0.5,
									borderRadius: 1.5,
									p: 1,
									border: `1px solid ${c.border}`,
									bgcolor:
										s._id === chatSessionId ? c.accentBand : "transparent",
									"&:hover": { bgcolor: c.band },
								}}
							>
								<Box
									onClick={() => loadChat(s._id)}
									sx={{ flex: 1, minWidth: 0, cursor: "pointer" }}
								>
									<Typography
										sx={{
											fontSize: 14,
											color: c.ink,
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}
									>
										{s.title || "Untitled"}
									</Typography>
									<Chip
										size="small"
										label={s.status}
										sx={{ height: 18, fontSize: 10, mt: 0.5 }}
									/>
								</Box>
								<IconButton
									size="small"
									aria-label="Delete conversation"
									onClick={(e) => {
										e.stopPropagation();
										deleteChat(s._id);
									}}
								>
									<DeleteOutlineIcon fontSize="small" />
								</IconButton>
							</Box>
						))}
					</Stack>
				)}
			</Paper>

			{/* Email dialog */}
			<Dialog open={Boolean(emailTarget)} onClose={() => setEmailTarget(null)}>
				<DialogTitle>Email report</DialogTitle>
				<DialogContent sx={{ minWidth: 360 }}>
					<Typography sx={{ fontSize: 14, color: c.muted, mb: 1.5 }}>
						Recipients (comma-separated). A short-lived download link is
						included.
					</Typography>
					<TextField
						fullWidth
						size="small"
						placeholder="name@example.com, other@example.com"
						value={emailRecipients}
						onChange={(e) => setEmailRecipients(e.target.value)}
						autoFocus
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setEmailTarget(null)}>Cancel</Button>
					<Button
						variant="contained"
						onClick={handleSendEmail}
						disabled={emailBusy || !emailRecipients.trim()}
					>
						{emailBusy ? "Sending…" : "Send"}
					</Button>
				</DialogActions>
			</Dialog>

			{toast && (
				<Box
					onClick={() => setToast("")}
					sx={{
						position: "fixed",
						bottom: 24,
						left: "50%",
						transform: "translateX(-50%)",
						bgcolor: c.ink,
						color: theme.palette.surface.light,
						px: 2,
						py: 1,
						borderRadius: 2,
						fontSize: 14,
						cursor: "pointer",
						zIndex: 1400,
					}}
				>
					{toast}
				</Box>
			)}
		</Box>
	);
};

export default AnalyticsAssistantTab;
